import * as React from 'react';

import { Button } from '@cian/ui-kit';
import { plural } from '@cian/utils/lib/shared/plural';
import { IVacancy } from '../../index';
import { decodeHtml } from '../../utils';
import * as styles from './vacancy_preview.css';
import { EEmployment } from '../../repositories/vacancies/entities/schemas/VacancySchema';
import { numberToPrettyString } from '@cian/utils';

interface IVacancyPreviewProps {
  vacancy: IVacancy;
}

export const formatPhone = (phone: string) =>
  `+7 ${phone.slice(0, 3)} ${phone.slice(3, 6)}-${phone.slice(6, 8)}-${phone.slice(8)}`;

export const formatSalary = (min: number | undefined, max: number | undefined): JSX.Element => {
  const currency: JSX.Element = <span className={styles['no_wrap']}>₽/мес.</span>;
  const formatVal = (val: number) => <span className={styles['no_wrap']}>{numberToPrettyString(val)}</span>;

  if (min !== undefined && max !== undefined) {
    return (
      <span>
        {formatVal(min)} - {formatVal(max)}&nbsp;{currency}
      </span>
    );
  } else if (min !== undefined) {
    return (
      <span>
        от {formatVal(min)} {currency}
      </span>
    );
  } else if (max !== undefined) {
    return (
      <span>
        до {formatVal(max)} {currency}
      </span>
    );
  }

  return <span>По договорённости</span>;
};

export const formatExperience = (exp: number | undefined): string =>
  exp ? `от ${exp} ${plural(exp, ['года', 'лет', 'лет'])}` : 'Можно без опыта';

interface IEmploymentTypes {
  [index: string]: string;
}

export const employmentTypes: IEmploymentTypes = {
  [EEmployment.Full]: 'Полная',
  [EEmployment.Part]: 'Частичная',
  [EEmployment.Piece]: 'Сдельная оплата',
};

const VacancyPreview = (props: IVacancyPreviewProps) => {
  const vacancy: IVacancy = props.vacancy;

  return (
    <div className={styles['panel']}>
      <div className={styles['head']}>
        <div className={styles['success-text']}>Ваша вакансия успешно опубликована</div>
        <div className={styles['success-text_sup']}>
          Чтобы увидеть её, перейдите к<a href="/vacancies">поиску вакансий</a>
        </div>
      </div>
      <div className={styles['vacancy']}>
        <div className={styles['specialization']}>{decodeHtml(vacancy.specialization)}</div>

        <div className={styles['info']}>
          <div className={styles['info-item']}>
            <div className={styles['info-item_head']}>Зарплата</div>
            <div className={styles['info-item_content']}>{formatSalary(vacancy.minSalary, vacancy.maxSalary)}</div>
          </div>
          <div className={styles['info-item']}>
            <div className={styles['info-item_head']}>Город</div>
            <div className={styles['info-item_content']}>{vacancy.region && vacancy.region.displayName}</div>
          </div>
          <div className={styles['info-item']}>
            <div className={styles['info-item_head']}>Опыт</div>
            <div className={styles['info-item_content']}>{formatExperience(vacancy.workExperience)}</div>
          </div>
          <div className={styles['info-item']}>
            <div className={styles['info-item_head']}>Занятость</div>
            <div className={styles['info-item_content']}>{employmentTypes[vacancy.employment]}</div>
          </div>
        </div>

        <div className={styles['description']}>{decodeHtml(vacancy.description)}</div>
        <div className={styles['contacts']}>
          {vacancy.contactPhone && <div>{formatPhone(vacancy.contactPhone)}</div>}
          {vacancy.contactEmail && <div>{vacancy.contactEmail}</div>}
        </div>

        <div className={styles['divider']} />

        <Button
          data-name="PreviewEditButton"
          size="XS"
          theme="fill_primary"
          onClick={() => (window.location.pathname = `/vacancies/edit/${vacancy.id}`)}
        >
          Редактировать
        </Button>
      </div>
    </div>
  );
};

export { VacancyPreview };
